/**
 * The creative pipeline: one brief, moving through stages, seen differently by
 * everyone who touches it.
 *
 * This module is the contract both sides agree on before a single row exists -
 * what the stages are, who may move a brief between them, what a brief is made
 * of, and how the team's naming convention is spelled. The API enforces it and
 * the web client draws it; neither owns it.
 *
 * Why so much of it is pure data: every rule here was read off two thousand
 * real Notion rows rather than invented, so the rules have to be inspectable
 * (and testable) rather than buried in route handlers.
 */
import type { ConceptCategory, ConceptFileNames } from "./naming.js";
import type { Role } from "./access.js";
import type { CdnStill } from "./storyboards.js";

/* ------------------------------------------------------------------- stages */

/**
 * Where a brief is. Text, never a pg enum - same as every other enumerated
 * column in this schema, so adding a stage is a deploy rather than a migration
 * with a lock on it.
 *
 * The walk, in order: a concept lands in `strategist_review`, where the
 * strategist reads the presentation, edits it and leaves notes before any
 * editor can see it; "ready for editors" is `assigned`; the editor starts
 * (`in_progress`) and delivers (`in_review`), where the strategist looks
 * again and either sends it back (`changes_requested` - read "needs
 * revision"; the stored value is older than the name) or forwards it to the
 * admin's final review (`pending_review`), which approves. Every concept,
 * written by the agent or by hand, starts in `strategist_review`, owned by a
 * strategist; there is no stage before it.
 *
 * `cancelled` is deliberately terminal-but-visible: Notion's real data has 234
 * cancelled rows and the team reads them, so "trash" is a lane, not a delete.
 */
export const PIPELINE_STAGES = [
  "strategist_review",
  "assigned",
  "in_progress",
  "in_review",
  "changes_requested",
  "pending_review",
  "approved",
  "shipped",
  "cancelled",
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export function isPipelineStage(value: unknown): value is PipelineStage {
  return typeof value === "string" && (PIPELINE_STAGES as readonly string[]).includes(value);
}

/**
 * Names accepted on the wire for a stage whose stored value is spelled
 * differently. `needs_revision` is what the team says and what the buttons
 * read; `changes_requested` is what every row, event and deployed agent
 * already carries, and renaming the value would mean rewriting history for a
 * label. Input only: nothing ever writes an alias.
 *
 * `draft` was the stage before strategist review and no longer exists; every
 * brief that was in it now sits in `strategist_review`, and a request that
 * still names it means that stage.
 */
export const STAGE_ALIASES = {
  needs_revision: "changes_requested",
  draft: "strategist_review",
} as const satisfies Record<string, PipelineStage>;

export type PipelineStageInput = PipelineStage | keyof typeof STAGE_ALIASES;

/** A stage as sent, alias or not, as the stored value; null for anything that is neither. */
export function normalizeStage(value: unknown): PipelineStage | null {
  if (isPipelineStage(value)) return value;
  // Own keys only: `"constructor" in {}` is true, and a stage named after a
  // prototype method must come back null, not as a function typed as a stage.
  if (typeof value === "string" && Object.hasOwn(STAGE_ALIASES, value)) {
    return STAGE_ALIASES[value as keyof typeof STAGE_ALIASES];
  }
  return null;
}

/**
 * The four columns the team already thinks in, carried over from the Notion
 * board so nobody has to relearn where their work lives. Stages are the truth;
 * lanes are how they are grouped on screen.
 */
export const PIPELINE_LANES = ["todo", "in_process", "complete", "trash"] as const;

export type PipelineLane = (typeof PIPELINE_LANES)[number];

export const STAGE_LANE: Record<PipelineStage, PipelineLane> = {
  strategist_review: "todo",
  assigned: "todo",
  in_progress: "in_process",
  in_review: "in_process",
  changes_requested: "in_process",
  pending_review: "in_process",
  approved: "complete",
  shipped: "complete",
  cancelled: "trash",
};

export function laneOf(stage: PipelineStage): PipelineLane {
  return STAGE_LANE[stage];
}

/** Stages in a lane, in the order they are drawn. */
export function stagesInLane(lane: PipelineLane): PipelineStage[] {
  return PIPELINE_STAGES.filter((stage) => STAGE_LANE[stage] === lane);
}

/**
 * A brief that is still moving. Used for "reassign is allowed on a live brief"
 * and for the at-risk derivations, which must never flag finished work.
 */
export function isLiveStage(stage: PipelineStage): boolean {
  return stage !== "shipped" && stage !== "cancelled";
}

/**
 * The stages an editor must not know exist.
 *
 * A concept in strategist review is being rewritten, re-framed and annotated
 * before it is handed over; an editor who can already see it reads a brief
 * that is about to change under them.
 * Enforced by `canSeeBrief` and by the SQL filter the lists use, and it has to
 * reach everything hung off the brief: its deck, its storyboard, its bells.
 */
export const EDITOR_HIDDEN_STAGES = ["strategist_review"] as const satisfies readonly PipelineStage[];

export function isHiddenFromEditors(stage: PipelineStage): boolean {
  return (EDITOR_HIDDEN_STAGES as readonly PipelineStage[]).includes(stage);
}

/* -------------------------------------------------------------- transitions */

/**
 * Who is allowed to make a move, expressed as a relationship rather than a
 * role: "the editor this brief is assigned to" is not the same claim as "an
 * editor", and conflating the two is how an editor ends up approving their own
 * work.
 */
export type TransitionActor =
  /**
   * The concept's strategist or its author; any strategist when it is for any
   * strategist. Admins always pass as well.
   */
  | "owner_strategist"
  /** The editor currently assigned. Admins always pass as well. */
  | "assigned_editor"
  /** Admins only - nobody else ships. */
  | "admin";

/**
 * Extra input a transition needs from the caller, beyond the brief itself.
 *
 * Only a note. The delivery folder used to travel here too, pasted by the
 * editor at the moment of delivering; it is now assigned up front by a
 * strategist or an admin and lives on the brief, so delivering carries nothing
 * and is refused until the folder is there.
 */
export interface TransitionInput {
  /** Required by `changes_requested` and `cancelled`; a move with no reason is unreviewable. */
  note?: string | null;
}

/**
 * Everything a guard is allowed to look at.
 *
 * Deliberately a flat projection rather than the full brief: guards run on the
 * server before a write and in the browser to grey out a button, and the
 * browser must not need the whole object graph to decide whether a button is
 * live.
 */
export interface TransitionBrief {
  stage: PipelineStage;
  editorId: number | null;
  /** The strategist who owns it, as a portal user id. */
  strategistUserId: number | null;
  /** The strategists row it is for. Null with `strategistUserId` null: any strategist. */
  strategistId: number | null;
  createdByUserId: number | null;
  dueOn: string | null;
  /** The Drive folder the video goes into. Set by a strategist or admin; delivering needs it. */
  deliveryUrl: string | null;
  /**
   * Hooks that are not placeholders - an empty rail is not a brief - plus the
   * hooks of the concept's presentation, when it has one.
   */
  realHookCount: number;
  /** A script in the brief, or a line with words in its presentation. */
  hasScript: boolean;
}

/** Named so the API can answer with a code and the UI can say why in Spanish. */
export type TransitionRefusal =
  | "forbidden_transition"
  | "not_allowed"
  | "not_assigned"
  | "editor_required"
  | "hook_required"
  | "script_required"
  | "delivery_required"
  | "note_required";

export type TransitionCheck = { ok: true } | { ok: false; reason: TransitionRefusal };

const OK: TransitionCheck = { ok: true };

interface TransitionRule {
  from: PipelineStage;
  to: PipelineStage;
  actors: TransitionActor[];
  /** Null when the move needs nothing but permission. */
  guard?: (brief: TransitionBrief, input: TransitionInput) => TransitionRefusal | null;
}

const requireNote = (_b: TransitionBrief, input: TransitionInput): TransitionRefusal | null =>
  (input.note ?? "").trim().length === 0 ? "note_required" : null;

/** Whether the brief has somewhere for the video to go. */
export function hasDeliveryFolder(brief: Pick<TransitionBrief, "deliveryUrl">): boolean {
  return (brief.deliveryUrl ?? "").trim().length > 0;
}

const requireDeliveryFolder = (brief: TransitionBrief): TransitionRefusal | null =>
  hasDeliveryFolder(brief) ? null : "delivery_required";

/**
 * A brief becomes real work when somebody could actually start it: an editor to
 * do it, something to open on, words to say. 45% of the archive's briefs would
 * fail this check, which is exactly why it exists - those were the ones that
 * came back as "¿qué hago con esto?".
 */
function readyToAssign(brief: TransitionBrief): TransitionRefusal | null {
  if (brief.editorId === null) return "editor_required";
  if (brief.realHookCount < 1) return "hook_required";
  if (!brief.hasScript) return "script_required";
  return null;
}

/**
 * The whole machine, as data. Anything not listed here is not a legal move.
 *
 * Two reviews bracket the editor's work. Before: a concept is released to
 * editors only from `strategist_review`, by a person (the agent's key is
 * refused that one move on the server). After: delivery goes to the
 * strategist, who sends it back or forwards it; only the admin approves, from
 * `pending_review`. There is no `in_review -> approved` any more - a request
 * for it from an old agent is translated to `pending_review` with a hint.
 */
export const TRANSITIONS: readonly TransitionRule[] = [
  // "Ready for editors": the one move that makes a concept visible to its
  // editor, so it is the one that checks there is an editor, a hook and a
  // script to see. The `assigned` bell rings here, not at creation.
  { from: "strategist_review", to: "assigned", actors: ["owner_strategist"], guard: readyToAssign },
  { from: "assigned", to: "in_progress", actors: ["assigned_editor"] },
  // Delivering needs somewhere to deliver to. The folder is the brief's, not
  // the request's: a strategist or admin assigns it before the work is done,
  // and an editor without one is told to ask rather than handed a text box.
  { from: "in_progress", to: "in_review", actors: ["assigned_editor"], guard: requireDeliveryFolder },
  // "Needs revision", from either reviewer. The note is the revision request
  // the editor reads in the chat; a move with no reason is unreviewable.
  { from: "in_review", to: "changes_requested", actors: ["owner_strategist"], guard: requireNote },
  { from: "pending_review", to: "changes_requested", actors: ["admin"], guard: requireNote },
  { from: "changes_requested", to: "in_progress", actors: ["assigned_editor"] },
  // "Send to admin": the strategist is done looking; the final word is not theirs.
  { from: "in_review", to: "pending_review", actors: ["owner_strategist"] },
  { from: "pending_review", to: "approved", actors: ["admin"] },
  { from: "approved", to: "shipped", actors: ["admin"] },
  // Steps back. "I pressed deliver on the wrong concept", "I pressed start by
  // accident", "I sent that to the admin too soon", "that was not ready for
  // editors after all", "I asked for the wrong revision", "I approved the
  // wrong one" are everyday slips, and the person who made one should be able
  // to unmake it without asking somebody else to. None needs a note and none
  // reaches past the stage before. Only taking a concept back from its editor
  // rings a bell (`concept_withdrawn`), because it disappears from their page;
  // the rest are quiet. There is no way out of shipped or cancelled.
  { from: "in_review", to: "in_progress", actors: ["assigned_editor"] },
  { from: "in_progress", to: "assigned", actors: ["assigned_editor", "owner_strategist"] },
  { from: "pending_review", to: "in_review", actors: ["owner_strategist"] },
  // "Back to New presentation": the editor stays pencilled in but can no
  // longer see it, until it is marked ready again.
  { from: "assigned", to: "strategist_review", actors: ["owner_strategist"] },
  // Undo a revision request: back to the reviewer's desk, the round with it.
  { from: "changes_requested", to: "in_review", actors: ["owner_strategist"] },
  // "Undo approval": the admin's own word, so only theirs to take back.
  { from: "approved", to: "pending_review", actors: ["admin"] },
  // Cancelling is expanded per source stage below, so the table stays one row
  // per legal move and nothing has to special-case a wildcard at read time.
  ...PIPELINE_STAGES.filter(isLiveStage).map(
    (from): TransitionRule => ({
      from,
      to: "cancelled",
      actors: ["owner_strategist"],
      guard: requireNote,
    }),
  ),
];

/**
 * Whether a move is a step back rather than a step forward.
 *
 * The machine treats both the same; the people around it do not. A step back is
 * not a delivery and not an assignment, so nothing is announced for it, and a
 * screen that lists "what you can do next" leaves it out unless it has a place
 * designed for undoing. Spelled as data so the table above and this answer
 * cannot disagree.
 */
const BACKWARD_MOVES: ReadonlyArray<readonly [from: PipelineStage, to: PipelineStage]> = [
  ["in_review", "in_progress"],
  ["in_progress", "assigned"],
  ["pending_review", "in_review"],
  ["assigned", "strategist_review"],
  ["changes_requested", "in_review"],
  ["approved", "pending_review"],
];

export function isBackwardMove(from: PipelineStage, to: PipelineStage): boolean {
  return BACKWARD_MOVES.some(([f, t]) => f === from && t === to);
}

/** The move into "needs revision", from either reviewer - what bumps the revision round. */
export function isRevisionRequest(from: PipelineStage, to: PipelineStage): boolean {
  return to === "changes_requested" && (from === "in_review" || from === "pending_review");
}

/** Every stage a brief could legally reach from here, ignoring who is asking. */
export function nextStages(from: PipelineStage): PipelineStage[] {
  return [...new Set(TRANSITIONS.filter((rule) => rule.from === from).map((rule) => rule.to))];
}

/* ------------------------------------------------------------- capabilities */

/**
 * The person asking, reduced to the four facts any pipeline decision turns on.
 *
 * `editorId` and `strategistId` are both here because one person can be both -
 * Gabriela wrote 998 scripts and produced 128 of them herself - so the old
 * assumption of one link per account does not survive contact with this data.
 */
export interface PipelineViewer {
  role: Role | null;
  userId: number | null;
  /** The roster row this account edits as, when it has one. */
  editorId: number | null;
  /** The strategist code this account writes as, when it has one. */
  strategistId: string | null;
  /**
   * Every account that is this same strategist, `userId` included: a person
   * who signs in with two Google accounts owns what either of them wrote.
   * Absent means just `userId`.
   */
  accountIds?: readonly number[];
  /**
   * The `strategists` row id behind `strategistId`, when known: a concept whose
   * `strategist_id` is this row is the person's, whichever account it names.
   */
  strategistRowId?: number | null;
}

/** The accounts a viewer writes as: their own, plus any others linked to the same strategist. */
export function viewerAccountIds(viewer: PipelineViewer): number[] {
  const ids = new Set(viewer.accountIds ?? []);
  if (viewer.userId !== null) ids.add(viewer.userId);
  return [...ids];
}

/**
 * The brief facts the visibility rules need. Satisfied by `TransitionBrief`.
 *
 * `stage` is here because visibility is stage-aware for editors: the same
 * brief is invisible to its own editor while it is in strategist review and
 * visible the moment it is released, and a rule that cannot see the stage
 * cannot say which.
 */
export interface ViewableBrief {
  stage: PipelineStage;
  editorId: number | null;
  strategistUserId: number | null;
  /** The strategists row it is for: null with `strategistUserId` null means any strategist. */
  strategistId: number | null;
  createdByUserId: number | null;
}

export function isAdmin(viewer: PipelineViewer): boolean {
  return viewer.role === "admin";
}

/**
 * Whoever wrote it, whether they are still the named strategist or not. Compared
 * as a person, not an account: the brief's strategist row is the viewer's, or it
 * names (or was written by) any account linked to the viewer's strategist.
 */
export function isBriefOwner(viewer: PipelineViewer, brief: ViewableBrief): boolean {
  if (viewer.userId === null) return false;
  const mine = viewerAccountIds(viewer);
  return (
    (viewer.strategistRowId != null && brief.strategistId === viewer.strategistRowId) ||
    (brief.strategistUserId !== null && mine.includes(brief.strategistUserId)) ||
    (brief.createdByUserId !== null && mine.includes(brief.createdByUserId))
  );
}

/** A concept with no strategist named is for any strategist: each of them may act on it as its own. */
export function isForAnyStrategist(brief: Pick<ViewableBrief, "strategistUserId" | "strategistId">): boolean {
  return brief.strategistUserId === null && brief.strategistId === null;
}

/**
 * A strategist acting as the concept's own: its named strategist or author, or
 * any strategist when none is named. What editing, releasing and reviewing test.
 */
export function actsAsOwnerStrategist(viewer: PipelineViewer, brief: ViewableBrief): boolean {
  return viewer.role === "strategist" && (isBriefOwner(viewer, brief) || isForAnyStrategist(brief));
}

export function isAssignedEditor(viewer: PipelineViewer, brief: ViewableBrief): boolean {
  return viewer.editorId !== null && brief.editorId === viewer.editorId;
}

/**
 * Who may open a brief.
 *
 * Strategists see the whole pipeline, not only their own: the board and table
 * are explicitly a view over everything, and Belén has to be able to read
 * Gabriela's briefs to avoid writing the same concept twice. What narrows to
 * "mine" is the *default list scope*, below - not the right to read. Editors
 * are the real boundary: their own assignments, and only once released -
 * never a concept still in strategist review. The SQL filter
 * the lists use says the same thing in its own language; the two must move
 * together.
 */
export function canSeeBrief(viewer: PipelineViewer, brief: ViewableBrief): boolean {
  if (isAdmin(viewer)) return true;
  if (viewer.role === "strategist") return true;
  if (viewer.role === "editor") return isAssignedEditor(viewer, brief) && !isHiddenFromEditors(brief.stage);
  return false;
}

/**
 * Who may change what a brief *says* - its hooks, script, notes, references.
 *
 * Never the editor: an editor who can rewrite the brief they were given can
 * also rewrite the thing they are about to be reviewed against. Shipped and
 * cancelled briefs are frozen for everyone, admins included, because they are
 * now history that other numbers are joined to.
 */
export function canEditBrief(viewer: PipelineViewer, brief: ViewableBrief & { stage: PipelineStage }): boolean {
  if (!isLiveStage(brief.stage)) return false;
  if (isAdmin(viewer)) return true;
  return actsAsOwnerStrategist(viewer, brief);
}

/** Anyone who can see it can talk about it. Comments are the review loop. */
export function canCommentOnBrief(viewer: PipelineViewer, brief: ViewableBrief): boolean {
  return canSeeBrief(viewer, brief);
}

/** Uploading VO or attachments: whoever owns the brief, plus the editor doing it. */
export function canAttachToBrief(
  viewer: PipelineViewer,
  brief: ViewableBrief & { stage: PipelineStage },
): boolean {
  if (!isLiveStage(brief.stage)) return false;
  return canEditBrief(viewer, brief) || isAssignedEditor(viewer, brief);
}

function actorMatches(actor: TransitionActor, viewer: PipelineViewer, brief: TransitionBrief): boolean {
  // Admins stand in for every actor except where a rule names `admin` alone,
  // which is the one place (shipping) where standing in is the whole point.
  if (isAdmin(viewer)) return true;
  switch (actor) {
    case "owner_strategist":
      return actsAsOwnerStrategist(viewer, brief);
    case "assigned_editor":
      return viewer.role === "editor" && isAssignedEditor(viewer, brief);
    case "admin":
      return false;
  }
}

/**
 * The one question the transition endpoint asks.
 *
 * Refusals are separated into "this move does not exist" and "not by you",
 * because they are different bugs: the first is a client that drew a button it
 * should not have, the second is a permission problem worth logging.
 */
export function canTransition(
  from: PipelineStage,
  to: PipelineStage,
  viewer: PipelineViewer,
  brief: TransitionBrief,
  input: TransitionInput = {},
): TransitionCheck {
  const rules = TRANSITIONS.filter((rule) => rule.from === from && rule.to === to);
  if (rules.length === 0) return { ok: false, reason: "forbidden_transition" };

  const permitted = rules.filter((rule) => rule.actors.some((actor) => actorMatches(actor, viewer, brief)));
  if (permitted.length === 0) {
    // An editor refused on their own brief's stage is almost always an
    // assignment problem rather than a role problem, and saying so saves a
    // support round trip.
    if (viewer.role === "editor" && !isAssignedEditor(viewer, brief)) {
      return { ok: false, reason: "not_assigned" };
    }
    return { ok: false, reason: "not_allowed" };
  }

  for (const rule of permitted) {
    const failure = rule.guard?.(brief, input) ?? null;
    if (failure === null) return OK;
    // Several rules can share a from/to pair only through the cancel expansion,
    // which all carry the same guard, so the first failure is the answer.
    return { ok: false, reason: failure };
  }
  return OK;
}

/**
 * The stages in which the editor may be chosen, changed or removed: before the
 * editor starts. Once the work is under way the assignment is history the
 * revision rounds and the payout are counted against, so it stays put.
 */
export const EDITOR_ASSIGNABLE_STAGES = ["strategist_review", "assigned"] as const satisfies readonly PipelineStage[];

export function isEditorAssignable(stage: PipelineStage): boolean {
  return (EDITOR_ASSIGNABLE_STAGES as readonly PipelineStage[]).includes(stage);
}

/** Why this viewer may not change the editor now, or null when they may. */
export type ReassignRefusal = "not_allowed" | "editor_locked";

export function reassignRefusal(viewer: PipelineViewer, brief: Pick<TransitionBrief, "stage">): ReassignRefusal | null {
  if (!isAdmin(viewer) && viewer.role !== "strategist") return "not_allowed";
  return isEditorAssignable(brief.stage) ? null : "editor_locked";
}

/**
 * Assigning, reassigning or unassigning the editor: any strategist or an admin,
 * while the concept is in strategist review or waiting for its editor to start.
 * It changes who, not where.
 */
export function canReassign(viewer: PipelineViewer, brief: Pick<TransitionBrief, "stage">): boolean {
  return reassignRefusal(viewer, brief) === null;
}

/**
 * Who may say where the video goes.
 *
 * Wider than `canEditBrief` in exactly one direction: any strategist, not only
 * the one who wrote the brief. The delivery folder is made by whoever is
 * setting up the Drive that week, which is not always the author, and a
 * folder is not part of what the brief *says* - changing it rewrites nothing
 * the editor is reviewed against. Never the editor: the whole point of
 * assigning it up front is that the editor is handed the destination rather
 * than asked for one.
 */
export function canSetDeliveryFolder(viewer: PipelineViewer, brief: { stage: PipelineStage }): boolean {
  if (!isLiveStage(brief.stage)) return false;
  return isAdmin(viewer) || viewer.role === "strategist";
}

/**
 * The list a role is shown by default, before any filter they choose.
 *
 * Separate from `canSeeBrief` on purpose: an editor *cannot* widen this, while
 * a strategist can - their default is their own work, and the board is the
 * deliberate act of asking for everything.
 */
export type BriefScope =
  | { kind: "editor"; editorId: number }
  /** `userIds`: every account that is this strategist, `userId` among them. */
  | { kind: "strategist"; userId: number; userIds?: readonly number[]; strategistRowId?: number | null }
  | { kind: "all" }
  | { kind: "none" };

export function defaultBriefScope(viewer: PipelineViewer): BriefScope {
  if (isAdmin(viewer)) return { kind: "all" };
  if (viewer.role === "strategist" && viewer.userId !== null) {
    return { kind: "strategist", userId: viewer.userId, userIds: viewerAccountIds(viewer), strategistRowId: viewer.strategistRowId ?? null };
  }
  if (viewer.role === "editor" && viewer.editorId !== null) {
    return { kind: "editor", editorId: viewer.editorId };
  }
  return { kind: "none" };
}

/** The widest scope a viewer may ask for, so a requested scope can be refused. */
export function canUseScope(viewer: PipelineViewer, scope: BriefScope): boolean {
  if (isAdmin(viewer)) return true;
  if (viewer.role === "strategist") return scope.kind !== "none";
  if (viewer.role === "editor") return scope.kind === "editor" && scope.editorId === viewer.editorId;
  return false;
}

/* ---------------------------------------------------------------- due dates */

/**
 * Five days, because that is what the work actually takes: the observed median
 * cycle across the archive is 5.2 days (p90 12.1). A default nobody has to
 * argue with is a default people leave alone.
 */
export const DEFAULT_DUE_DAYS = 5;

/**
 * How long a brief may sit in review before it is flagged. 48h is just past the
 * p75 of real comment-thread spans (41.5h), so the flag means "this one stalled",
 * not "this one is normal".
 */
export const REVIEW_SLA_HOURS = 48;

/** `YYYY-MM-DD`, the only date shape this app passes around. */
export type DateOnly = string;

const DAY_MS = 86_400_000;

function toUtcDate(iso: DateOnly): Date {
  return new Date(`${iso}T00:00:00Z`);
}

function toIso(date: Date): DateOnly {
  return date.toISOString().slice(0, 10);
}

/** Assignment date plus the default, used when a strategist does not set one. */
export function defaultDueDate(from: DateOnly, days: number = DEFAULT_DUE_DAYS): DateOnly {
  return toIso(new Date(toUtcDate(from).getTime() + days * DAY_MS));
}

/** Negative when overdue. Whole days, computed in UTC so no timezone flips it. */
export function daysUntilDue(dueOn: DateOnly, today: DateOnly): number {
  return Math.round((toUtcDate(dueOn).getTime() - toUtcDate(today).getTime()) / DAY_MS);
}

/**
 * The colour of the due pill, decided once here rather than in each screen that
 * draws one.
 *
 * `none` is its own state rather than folding into `ok`: a brief with no due
 * date is not on time, it is unscheduled, and greying it says so.
 */
export type DueState = "none" | "ok" | "soon" | "overdue";

export function dueState(dueOn: DateOnly | null, today: DateOnly): DueState {
  if (!dueOn) return "none";
  const days = daysUntilDue(dueOn, today);
  if (days < 0) return "overdue";
  return days <= 1 ? "soon" : "ok";
}

/**
 * Stages where a passed due date is nobody's lateness: the editor has not been
 * handed the work yet (a concept in strategist review) or has already
 * handed it back (waiting on the admin's final word). The clock is the
 * editor's, and it only runs while the brief is theirs.
 */
export const OVERDUE_EXEMPT_STAGES = ["strategist_review", "pending_review"] as const satisfies readonly PipelineStage[];

/** Derived, never stored - a stored "overdue" is wrong the moment the clock moves. */
export function isOverdue(brief: { stage: PipelineStage; dueOn: DateOnly | null }, today: DateOnly): boolean {
  if (!isLiveStage(brief.stage)) return false;
  if ((OVERDUE_EXEMPT_STAGES as readonly PipelineStage[]).includes(brief.stage)) return false;
  return dueState(brief.dueOn, today) === "overdue";
}

/** A review nobody has picked up. `since` is when the brief entered `in_review`. */
export function isReviewStale(since: string | Date, now: Date = new Date()): boolean {
  const at = since instanceof Date ? since : new Date(since);
  return now.getTime() - at.getTime() > REVIEW_SLA_HOURS * 3_600_000;
}

/* ------------------------------------------------------- naming convention */

/**
 * The delivery name the team writes on every finished file.
 *
 *   EDITOR|CONCEPT|PRODUCT|[ITRn|]HOOKn|TECHNIQUE|ANGLE|GENDER|DURATION|STRATEGIST|
 *   CRIS|2124|GUM|HOOK1|UGC|PROBLEM SOLVE|MIX|1:15|GAB|
 *   RAN|2077|PASTE|ITR1|HOOK2|AI|CURIOSITY GAP|MALE|1:05|GAB|
 *
 * Only 75 of 2,239 Notion rows ever got one filled in, and all 75 parse - which
 * is the argument for generating it instead of asking for it. The portal knows
 * every part already; the one thing it was ever used for was copy-paste into
 * Drive, and "me ayudas con el naming por fa" is a message nobody should have
 * to send again.
 *
 * The trailing pipe is part of the convention, not an accident: it is how the
 * team spots a truncated paste.
 */
export interface NamingConvention {
  /** Editor tag, e.g. `CRIS`, `DAV`, `ESP`. */
  editor: string;
  /** The 4-digit concept id. */
  concept: number;
  /** Product code as the convention spells it: `GUM`, `PASTE`, `NANO`. */
  product: string;
  /** Re-edit round. Null when this is the original cut. */
  iteration: number | null;
  hook: number;
  /** `AI+UGC`, `ANIMATION`, `WHITEBOARD`, `KINETIC TEXT`... */
  technique: string;
  /** `PROBLEM SOLVE`, `FACT`, `CURIOSITY GAP`... */
  angle: string;
  /** Voice/talent: `MALE`, `FEMALE`, `MIX`, `N/A`. */
  gender: string;
  /** Clip length in whole seconds; rendered `m:ss`. */
  durationSeconds: number;
  /** Strategist tag, `GAB` or `BEL`. */
  strategist: string;
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.round(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

/** Seconds from `m:ss`, or null if it is not that shape. */
export function parseDuration(text: string): number | null {
  const match = /^(\d{1,3}):([0-5]\d)$/.exec(text.trim());
  if (!match) return null;
  return parseInt(match[1]!, 10) * 60 + parseInt(match[2]!, 10);
}

/**
 * Build the string. Every part is upper-cased and trimmed, because the 75 real
 * values are and because a lower-case token would break the parsers that read
 * these names back out of Drive.
 */
export function formatNamingConvention(parts: NamingConvention): string {
  const fields = [
    parts.editor,
    String(parts.concept),
    parts.product,
    ...(parts.iteration === null ? [] : [`ITR${parts.iteration}`]),
    `HOOK${parts.hook}`,
    parts.technique,
    parts.angle,
    parts.gender,
    formatDuration(parts.durationSeconds),
    parts.strategist,
  ];
  return `${fields.map((f) => String(f).trim().toUpperCase()).join("|")}|`;
}

export type NamingParseError =
  | "field_count"
  | "editor"
  | "concept"
  | "product"
  | "hook"
  | "duration"
  | "strategist";

export type NamingParseResult =
  | { ok: true; value: NamingConvention }
  | { ok: false; errors: NamingParseError[] };

/**
 * Read one back.
 *
 * Positional rather than token-sniffing, unlike `parse.ts`: this field is
 * written by the portal to a fixed grammar, so a positional read is the thing
 * that catches a malformed paste instead of silently half-understanding it.
 *
 * Two real-world dents are handled on purpose:
 *  - `DAV|ITR2084|GUM|HOOK1|...` - one row glued ITR onto the concept instead of
 *    giving it its own field. It means iteration 1 of concept 2084, and
 *    dropping the row would lose a real delivered file.
 *  - the trailing pipe leaves an empty last field, which is expected, not an
 *    error.
 */
export function parseNamingConvention(raw: string): NamingParseResult {
  const text = (raw ?? "").trim();
  const fields = text.split("|").map((f) => f.trim());
  // The trailing pipe produces one empty tail field; anything else empty at the
  // end is a truncated paste and must not be padded away.
  while (fields.length > 0 && fields[fields.length - 1] === "") fields.pop();

  const errors: NamingParseError[] = [];
  if (fields.length !== 9 && fields.length !== 10) return { ok: false, errors: ["field_count"] };

  const hasItrField = fields.length === 10;
  const editor = (fields[0] ?? "").toUpperCase();
  let conceptText = (fields[1] ?? "").toUpperCase();
  const product = (fields[2] ?? "").toUpperCase();

  let iteration: number | null = null;
  if (hasItrField) {
    const itr = /^ITR\s*(\d{1,2})$/.exec((fields[3] ?? "").toUpperCase());
    // A 10-field name whose 4th field is not ITRn is not this grammar.
    if (!itr) return { ok: false, errors: ["field_count"] };
    iteration = parseInt(itr[1]!, 10);
  }

  // The glued form. Checked after the field-count split so it cannot be
  // confused with a properly separated ITR field.
  const glued = /^ITR\s*(\d{4})$/.exec(conceptText);
  if (glued) {
    conceptText = glued[1]!;
    if (iteration === null) iteration = 1;
  }

  const rest = hasItrField ? fields.slice(4) : fields.slice(3);
  const [hookText = "", technique = "", angle = "", gender = "", durationText = "", strategist = ""] = rest;

  if (!/^[A-Z]{2,12}$/.test(editor)) errors.push("editor");
  if (!/^\d{3,6}$/.test(conceptText)) errors.push("concept");
  if (product.length === 0) errors.push("product");

  const hook = /^HOOK\s*(\d{1,2})$/.exec(hookText.toUpperCase());
  if (!hook) errors.push("hook");

  const durationSeconds = parseDuration(durationText);
  if (durationSeconds === null) errors.push("duration");
  if (!/^[A-Z]{2,6}$/.test(strategist.toUpperCase())) errors.push("strategist");

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    value: {
      editor,
      concept: parseInt(conceptText, 10),
      product,
      iteration,
      hook: parseInt(hook![1]!, 10),
      technique: technique.toUpperCase(),
      angle: angle.toUpperCase(),
      gender: gender.toUpperCase(),
      durationSeconds: durationSeconds!,
      strategist: strategist.toUpperCase(),
    },
  };
}

/* ------------------------------------------------------------ lookup tables */

/**
 * The three products, and every spelling Notion has for them.
 *
 * Aliases live here rather than in the importer because the composer needs them
 * too: someone will paste "Scandi Gum" into a brief long after the import ran.
 */
export const PRODUCTS = [
  { code: "GUM", name: "Scandi Gum", namingCode: "GUM", aliases: ["Scandi", "Scandi Gum", "Gum"] },
  { code: "PASTE_BRUSH", name: "Paste & Brush", namingCode: "PASTE", aliases: ["Paste", "Paste & Brush", "Paste and Brush"] },
  { code: "NANO_BUNDLE", name: "Nano Bundle", namingCode: "NANO", aliases: ["Nano", "Nano Bundle"] },
] as const;

export type ProductCode = (typeof PRODUCTS)[number]["code"];

/** Case- and space-insensitive, because Notion's select values are hand-typed. */
export function productCodeFromAlias(value: string | null | undefined): ProductCode | null {
  const needle = (value ?? "").trim().toLowerCase();
  if (!needle) return null;
  for (const product of PRODUCTS) {
    if (product.code.toLowerCase() === needle) return product.code;
    if (product.namingCode.toLowerCase() === needle) return product.code;
    if (product.aliases.some((alias) => alias.toLowerCase() === needle)) return product.code;
  }
  return null;
}

/**
 * What a concept's ad sells: the gum, or the paste + brush bundle. Every
 * concept has one; a concept nobody chose for is `gum`.
 */
export const CONCEPT_PRODUCTS = ["gum", "brush_bundle"] as const;
export type ConceptProduct = (typeof CONCEPT_PRODUCTS)[number];
export const DEFAULT_CONCEPT_PRODUCT: ConceptProduct = "gum";

export function isConceptProduct(value: unknown): value is ConceptProduct {
  return typeof value === "string" && (CONCEPT_PRODUCTS as readonly string[]).includes(value);
}

/** The creatives sheet's Product column (H), exactly as the sheet spells it. */
export const PRODUCT_SHEET_LABEL: Record<ConceptProduct, string> = {
  gum: "Gum",
  brush_bundle: "Paste + Brush",
};

/** Who may set a concept's product: any strategist or admin who can see it. */
export function canSetBriefProduct(viewer: PipelineViewer): boolean {
  return viewer.role === "admin" || viewer.role === "strategist";
}

/** Notion's Framework select, seeded so it is a picker rather than free text. */
export const FRAMEWORKS = [
  "Direct Response",
  "Problem Solution",
  "Storytelling",
  "Social Proof",
  "Educational",
  "Comparison",
] as const;

/** How it was made. `IA` is Spanish for AI and is folded into it on import. */
export const TECHNIQUES = [
  "AI",
  "UGC",
  "AI+UGC",
  "ANIMATION",
  "ANIMATION+IA",
  "ANIMATION+UGC",
  "WHITEBOARD",
  "KINETIC TEXT",
] as const;

/** The opening move. Read off the 75 filled naming conventions, not invented. */
export const ANGLES = [
  "PROBLEM SOLVE",
  "FACT",
  "QUESTION",
  "CONFESSION",
  "CURIOSITY GAP",
  "CONTRADICTION",
  "STAKES/TENSION",
] as const;

/** Voice/talent in the naming convention. `N/A` is real: animation has no talent. */
export const GENDERS = ["MALE", "FEMALE", "MIX", "N/A"] as const;

/** What a brief produces. Static and gif exist only in the archive. */
export const BRIEF_TYPES = ["video", "static", "gif"] as const;
export type BriefType = (typeof BRIEF_TYPES)[number];

/* ------------------------------------------------------------------ content */

/**
 * One of the three openings. Two of every three hooks were being lost on import
 * until `<br>` was normalised first, which is why `isPlaceholder` is a real
 * field: a slot the strategist left as "pendiente" must look different from one
 * the parser failed to find.
 */
export interface BriefHook {
  id: number;
  position: number;
  body: string;
  /** The Spanish stage direction that came with it, when there was one. */
  direction: string | null;
  isPlaceholder: boolean;
  placeholderNote: string | null;
  /** Supercut genre: the clip this hook is lifted from. */
  sourceClipUrl: string | null;
  sourceTimecode: string | null;
  voFileId: number | null;
}

/**
 * The instructions around the script. `must_do` is the red span - 45% of
 * Gabriela's briefs carry one and it is the highest-signal line in the
 * document, so it gets its own slot rather than being buried in the body.
 */
export const NOTE_KINDS = ["must_do", "note", "direction"] as const;
export type NoteKind = (typeof NOTE_KINDS)[number];

export interface BriefNote {
  id: number;
  position: number;
  kind: NoteKind;
  body: string;
  /** `es` for nearly all of them; kept so a mixed brief renders right. */
  language: string | null;
}

/** How much of the import we trust. Shown as "ver original" when it is not much. */
export const IMPORT_CONFIDENCE = ["clean", "partial", "raw_only"] as const;
export type ImportConfidence = (typeof IMPORT_CONFIDENCE)[number];

export interface BriefScript {
  scriptMd: string;
  /** Always kept, for every import - the parser will be wrong sometimes. */
  rawMarkdown: string;
  importConfidence: ImportConfidence;
  parsedAt: string | null;
}

/** Everything the library can hold. `web` is the catch-all, not a failure. */
export const ASSET_KINDS = [
  "drive_file",
  "drive_folder",
  "tiktok",
  "instagram",
  "pinterest",
  "youtube",
  "fb_ad_library",
  "atria",
  "gethookd",
  "gdoc",
  "web",
] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

/** A link is either there, gone, or not looked at yet. 404 is data, not an error. */
export const ASSET_STATUS = ["ok", "gone", "unchecked"] as const;
export type AssetStatus = (typeof ASSET_STATUS)[number];

export interface LibraryAsset {
  id: number;
  kind: AssetKind;
  externalId: string;
  urlCanonical: string;
  title: string | null;
  /** User-editable; the one field the team owns on a borrowed link. */
  description: string | null;
  meta: Record<string, unknown> | null;
  status: AssetStatus;
  timesUsed: number;
  firstSeenAt: string;
  checkedAt: string | null;
}

export interface BriefRef {
  id: number;
  position: number;
  asset: LibraryAsset;
  /** The brief's own words around the link, which is what makes it searchable. */
  contextLine: string | null;
}

export const FILE_KINDS = ["vo", "attachment"] as const;
export type FileKind = (typeof FILE_KINDS)[number];

export interface BriefFile {
  id: number;
  kind: FileKind;
  position: number;
  label: string | null;
  originalName: string;
  contentType: string;
  bytes: number;
  durationMs: number | null;
  /** ElevenLabs voice/model/params, read out of the file name on import. */
  voiceMeta: Record<string, unknown> | null;
  source: "notion_import" | "upload";
  createdAt: string;
  /** Points at our own endpoint; storage keys never reach the browser. */
  url: string;
}

/**
 * What a brief comment is. `comment` is something somebody typed; `revision`
 * is the message the needs-revision transition writes with the reviewer's
 * note, so the request reaches the editor inside the chat, highlighted, with
 * the round it opened.
 */
export const BRIEF_COMMENT_KINDS = ["comment", "revision"] as const;
export type BriefCommentKind = (typeof BRIEF_COMMENT_KINDS)[number];

/** One emoji on one comment, counted, with whether the reader is among them. The storyboard thread's shape. */
export interface BriefCommentReaction {
  emoji: string;
  count: number;
  mine: boolean;
  names: string[];
}

export interface BriefComment {
  id: number;
  parentId: number | null;
  authorUserId: number | null;
  authorName: string;
  authorPhotoUrl: string | null;
  bodyMd: string;
  mentions: number[];
  createdAt: string;
  editedAt: string | null;
  deletedAt: string | null;
  kind: BriefCommentKind;
  /** For a `revision` message: the round it opened. */
  revisionRound: number | null;
  /** How many times the body was edited; the earlier bodies are kept. */
  editCount: number;
  /** The author's role now; `null` for an imported author with no account. A bubble tells the two sides apart by it. */
  authorRole: Role | null;
  /** Empty on a deleted comment. `mine` is computed for the reader. */
  reactions: BriefCommentReaction[];
}

/**
 * What happened, in order. Append-only: the log is the audit trail.
 *
 * `release` is a concept handed to its editor (`strategist_review -> assigned`);
 * `forward` is a delivery passed on to the admin's final review
 * (`in_review -> pending_review`). `reject` remains the needs-revision move,
 * from either reviewer, and carries the round in its payload.
 */
export const EVENT_KINDS = [
  "stage",
  "assign",
  "comment",
  "deliver",
  "approve",
  "reject",
  "cancel",
  "edit",
  "import",
  "release",
  "forward",
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export interface BriefEvent {
  id: number;
  kind: EventKind;
  fromStage: PipelineStage | null;
  toStage: PipelineStage | null;
  actorUserId: number | null;
  actorName: string | null;
  actorRole: Role | null;
  note: string | null;
  payload: Record<string, unknown> | null;
  at: string;
}

/* ------------------------------------------------------------ presentations */

/**
 * Where a hosted deck is in its publish.
 *
 * Text and a fixed list, like the stages above: `uploading` from the moment
 * the agent declares its manifest of hashes until every blob has been seen in
 * the bucket, `ready` once it can be served, `failed` when a publish was given
 * up on. Only `ready` is ever opened, which is why a deck cannot be reached as
 * a page of half-uploaded videos.
 */
export const PRESENTATION_STATUSES = ["uploading", "ready", "failed"] as const;

export type PresentationStatus = (typeof PRESENTATION_STATUSES)[number];

export function isPresentationStatus(value: unknown): value is PresentationStatus {
  return typeof value === "string" && (PRESENTATION_STATUSES as readonly string[]).includes(value);
}

/**
 * Who may open a hosted deck.
 *
 * `signed_in` is the default and what every deck was until this existed: any
 * portal account with a role. `public` is the deliberate exception an admin
 * makes for one deck - anyone on the internet with the link, no sign-in, no
 * ticket - for the day a deck has to reach a client or a freelancer who has no
 * account. It is a per-deck switch and only an admin can throw it, and the
 * server re-reads it on every request, so switching back is honoured at once.
 *
 * `admins` is narrower than either: real admins only (a Master AI artifact by
 * default, until an admin widens it).
 */
export const PRESENTATION_VISIBILITIES = ["admins", "signed_in", "public"] as const;

export type PresentationVisibility = (typeof PRESENTATION_VISIBILITIES)[number];

export function isPresentationVisibility(value: unknown): value is PresentationVisibility {
  return typeof value === "string" && (PRESENTATION_VISIBILITIES as readonly string[]).includes(value);
}

/**
 * A stored visibility read back, failing closed: only the exact string
 * `public` opens a deck to the internet; `signed_in`, and a null or a missing
 * value (a left join, a row from before the column), read as `signed_in`; any
 * other string - `admins`, a typo, a value a later build wrote - reads as
 * `admins`, the narrowest.
 */
export function presentationVisibilityOf(value: unknown): PresentationVisibility {
  if (value === "public") return "public";
  if (value === "signed_in" || value === null || value === undefined) return "signed_in";
  return "admins";
}

/**
 * What a hosted bundle is (`presentations.kind`): an ad `deck` (every deck
 * before this existed), a `report`, a `showcase` of work, or any other `page`.
 * The Artifacts page filters on it; serving does not depend on it.
 */
export const ARTIFACT_KINDS = ["deck", "report", "showcase", "page"] as const;

export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

export function isArtifactKind(value: unknown): value is ArtifactKind {
  return typeof value === "string" && (ARTIFACT_KINDS as readonly string[]).includes(value);
}

/**
 * Where hosted decks live, outside `/api` and outside the SPA's routes: the
 * API serves these paths itself, because a deck is 199 files of mp4 and jpg
 * rather than a JSON document, and because it needs its own Content-Security-
 * Policy for the inline script and style every deck carries.
 *
 * One constant so the mount, the login redirect and the links on a card can
 * never drift apart.
 */
export const PRESENTATION_BASE_PATH = "/p";

/**
 * The URL of a deck. The trailing slash is not cosmetic: every asset inside a
 * deck is referenced relatively, and without it the browser resolves
 * `assets/shots/x.mp4` against `/p/` and asks for a file that does not exist.
 */
export function presentationPath(slug: string): string {
  return `${PRESENTATION_BASE_PATH}/${slug}/`;
}

/** Lowercase words joined by single hyphens. What `presentationSlugBase` produces, plus its hex tail. */
export const PRESENTATION_SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function isPresentationSlug(value: unknown): value is string {
  return typeof value === "string" && value.length <= 80 && PRESENTATION_SLUG_RE.test(value);
}

/**
 * The readable half of a slug: the title, kebab-cased, accents folded, capped
 * short enough to stay readable in a chat message. The caller appends the
 * random tail, because two decks may honestly share a title and only one of
 * them can own `encias-sangrantes`.
 *
 * Falls back to `deck` rather than the empty string: a title written entirely
 * in punctuation or in a script we do not fold still has to produce a URL.
 */
export function presentationSlugBase(title: string): string {
  const folded = title
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/g, "");
  return folded === "" ? "deck" : folded;
}

/**
 * True when `?next=` may be followed after sign-in.
 *
 * A deck link is the one thing that sends a signed-out person to the login
 * screen, so the login screen is the one place that follows a URL it was
 * handed - and an open redirect there is a real one. Only our own deck paths
 * qualify: one leading slash (`//host` is another origin), `/p/` and nothing
 * clever, no backslash, which some browsers still read as a slash, and no dot
 * segment, which the browser would normalise back out of the deck's prefix.
 */
export function isSafeDeckRedirect(next: string | null | undefined): next is string {
  if (typeof next !== "string") return false;
  if (!next.startsWith(`${PRESENTATION_BASE_PATH}/`)) return false;
  if (next.startsWith("//") || next.includes("\\")) return false;
  if (next.includes("://")) return false;
  // A `..` segment cannot reach another origin, but `/p/../api/admin` does
  // start with `/p/` and the browser normalises it to `/api/admin` before the
  // request leaves, which would quietly turn "follow the deck link" into "go
  // anywhere in the app". The promise here is that what comes back is a deck,
  // so a path that is not one is not safe by this definition. Only the path is
  // normalised, so the query and fragment are cut off first.
  const path = next.split(/[?#]/, 1)[0] ?? "";
  return !path.split("/").some((segment) => segment === "." || segment === "..");
}

/**
 * One file of a published deck, as the API reports it. The storage key is
 * deliberately absent: a bucket path is a permanent credential and never
 * leaves the server.
 */
export interface PresentationFile {
  /** Deck-relative, exactly as the HTML references it: `assets/shots/skeleton-L01s1.mp4`. */
  path: string;
  /** The content address of the bytes, and the ETag the deck is served with. */
  sha256: string;
  contentType: string;
  bytes: number;
  uploadedAt: string;
}

/** A hosted deck as a list row. */
export interface PresentationSummary {
  id: number;
  slug: string;
  title: string;
  brand: string | null;
  status: PresentationStatus;
  /** Bumped by every finished publish; the slug, and so the URL, never changes. */
  version: number;
  /** The file `/p/<slug>/` serves, relative to the deck's root. */
  entry: string;
  /** What the current version holds. Null until the first publish was finalised. */
  fileCount: number | null;
  bytes: number | null;
  /** `/p/<slug>/`. Always our own origin; behind sign-in unless `visibility` says otherwise. */
  url: string;
  /** Who may open it. `signed_in` unless an admin made this one deck public (`admins`: real admins only). */
  visibility: PresentationVisibility;
  /** What it is; `deck` when absent (an older server). */
  kind?: ArtifactKind;
  /** The 16:9 still, served from a permission-checked endpoint. Null until one is uploaded. */
  thumbUrl: string | null;
  /** Whatever the agent called the run that built it. Shown, never parsed. */
  sourceLabel: string | null;
  /** The brief this deck was attached to, when one was. */
  briefId: number | null;
  briefRef: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

/** A hosted deck with its manifest: what the agent reads back to check a publish. */
export interface Presentation extends PresentationSummary {
  /** The account the publishing key acts as. */
  createdByUserId: number | null;
  createdByName: string | null;
  /** The key itself, by name - "which agent", asked separately from "on whose authority". */
  createdByKeyName: string | null;
  files: PresentationFile[];
}

/* --------------------------------------------------------------- agent API */

/**
 * What a machine key is allowed to write.
 *
 * Every key issued before this existed carries none of them, and none is the
 * default, so the read-only data API keeps behaving exactly as it did: scopes
 * only ever add. They are strings rather than a bitfield because they are
 * typed by a person at mint time and read back in `api-key list`, and because
 * a scope nobody granted should be unreadable rather than silently zero.
 *
 * The two `sync:` scopes are split the way they are because one of the keys
 * lives on somebody else's laptop. `sync:report` says what a machine has and
 * what it did with what it was handed; `sync:admin` decides what it is handed.
 * A bundle rewrites files on that machine, so the key sitting on it must not be
 * able to push one - a laptop key that could would be a way to write code onto
 * every other machine in the fleet. That separation is the whole reason there
 * are two scopes here rather than one `sync:write`.
 *
 * `translations:write` uploads a translated ad and links it to its original
 * (docs/translations/01-plan.md), and reads the library back: reading is
 * included because the uploader has to check what is already there.
 *
 * `worker:run` is the worker machine's key (docs/worker/01-plan.md): it claims
 * queued jobs, renews their leases, reports progress and results, and stores
 * what a job makes. It holds no database credentials; this is its only door.
 */
export const AGENT_API_SCOPES = [
  "presentations:write",
  "briefs:read",
  "briefs:write",
  "sync:report",
  "sync:admin",
  "translations:write",
  "worker:run",
  /** The cloud agents' deploy: registers releases and agent definitions, reads accepted knowledge edits (`/agent-api/v1/agents/*`). */
  "agents:deploy",
] as const;

export type AgentApiScope = (typeof AGENT_API_SCOPES)[number];

export function isAgentApiScope(value: unknown): value is AgentApiScope {
  return typeof value === "string" && (AGENT_API_SCOPES as readonly string[]).includes(value);
}

/**
 * Scopes that carry another one with them.
 *
 * `briefs:write` implies `briefs:read`: a key that may create and assign
 * concepts has to be able to see the queue it is assigning into, and making
 * every existing writer key be re-minted to learn that would be ceremony. The
 * reverse is never true - reading the pipeline grants no write.
 */
const IMPLIED_SCOPES: Partial<Record<AgentApiScope, readonly AgentApiScope[]>> = {
  "briefs:read": ["briefs:write"],
};

/** True when a key's stored scopes cover the one a route asks for, directly or by implication. */
export function hasAgentScope(scopes: readonly string[], scope: AgentApiScope): boolean {
  if (scopes.includes(scope)) return true;
  return (IMPLIED_SCOPES[scope] ?? []).some((wider) => scopes.includes(wider));
}

/* ------------------------------------------------------ machine key admin */

/**
 * The account a machine key acts as, as the keys page shows it.
 *
 * Only what an admin has to read to judge a key: a key acting as a strategist
 * can create real work for real editors, so the page names that person rather
 * than printing a user id.
 */
export interface ApiKeyActor {
  userId: number;
  email: string;
  /** The portal name, falling back to the Google name; null when neither is set. */
  name: string | null;
  role: Role | null;
}

/**
 * One key as an admin sees it. Never the key itself: `keyPrefix` is the first
 * twelve characters, which is all that is stored beside the hash and all
 * anybody needs to tell two keys apart in a list.
 */
export interface ApiKeyInfo {
  id: number;
  name: string;
  keyPrefix: string;
  scopes: string[];
  actsAs: ApiKeyActor | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** POST /api/admin/api-keys. `userId` null mints a read-only data-API key. */
export interface ApiKeyCreateRequest {
  name: string;
  scopes: AgentApiScope[];
  userId: number | null;
}

/**
 * The one response that carries key material, and the only time it exists.
 *
 * `plaintext` is never stored - the row holds a SHA-256 of it - so it cannot be
 * shown again, by this API or by anyone with the database. `shownOnce` says so
 * in the payload rather than only in the UI, because the next reader of this
 * response may be a script.
 */
export interface ApiKeyCreateResponse {
  key: ApiKeyInfo;
  plaintext: string;
  shownOnce: true;
  /** Plain words for whoever is looking at it, in English; the UI has its own. */
  notice: string;
}

/**
 * POST /api/admin/api-keys/:id/revoke.
 *
 * `cacheSeconds` is not decoration: both APIs cache key lookups, so a revoked
 * key keeps working for up to that long and whoever revoked it should be told
 * rather than left watching a key they just killed answer a request.
 */
export interface ApiKeyRevokeResponse {
  id: number;
  name: string;
  cacheSeconds: number;
}

/** The brief as a list row: enough to draw a card, nothing more. */
/**
 * The deck an editor actually works from.
 *
 * Every concept gets one: hooks, script, voice-overs and references laid out
 * slide by slide, hosted by us. The portal's job around it is small on purpose
 * - show it, let them open it, and let them say where the work got to - so the
 * brief page leads with this and keeps the written fallback underneath.
 */
export interface BriefPresentation {
  /** Where the deck lives. Opened in a new tab until it is embedded. */
  url: string | null;
  /** 16:9 still, served from our own origin. Null until one is uploaded. */
  thumbUrl: string | null;
  /**
   * The rest is the registered deck, when the deck is one we host: `url` is
   * then `/p/<slug>/` and these say which row it came from. They are optional
   * rather than nullable because a brief that points at a deck hosted
   * somewhere else has no row of ours to describe, and because `url` stays the
   * single field the UI reads either way.
   */
  id?: number | null;
  slug?: string | null;
  version?: number | null;
  status?: PresentationStatus | null;
  visibility?: PresentationVisibility | null;
}

export interface BriefSummary {
  id: number;
  ref: string;
  title: string;
  /**
   * What a card or a page heading shows. Notion titles were the concept code
   * and nothing else ("CPT - 2106"), which on a card only repeats the ref
   * printed above it, so a code-only title gives way to the first written hook.
   */
  headline: string;
  conceptNumber: number | null;
  iteration: number | null;
  productCode: ProductCode | null;
  /** What the ad sells; `gum` unless somebody chose the paste + brush bundle. */
  product: ConceptProduct;
  type: BriefType | null;
  stage: PipelineStage;
  editorId: number | null;
  editorName: string | null;
  strategistUserId: number | null;
  /** The strategist's name; null with `isForAnyStrategist` true means any strategist. */
  strategistName: string | null;
  /** The strategists row it is for. Null with `strategistUserId` null: any strategist. */
  strategistId: number | null;
  /**
   * The strategist it belongs to, as a person: `strategistId`, else the
   * strategists row its strategist's account is linked to. Null for any
   * strategist, or an account linked to nobody. What the concepts page groups
   * "Belén's concepts" by, so two accounts of one person make one section.
   * Lists only.
   */
  ownerStrategistId?: number | null;
  /** Who created it: a strategist in the composer, or the account behind the agent's key. */
  createdByUserId: number | null;
  dueOn: DateOnly | null;
  /** The Drive folder the video goes into, assigned by a strategist or admin. */
  deliveryUrl: string | null;
  /**
   * The folder's own name in Drive, looked up when the link was saved. Null
   * when the link points somewhere the service account cannot see, in which
   * case the UI shows the link itself.
   */
  deliveryFolderName: string | null;
  good: boolean;
  isArchived: boolean;
  /** False for imported archive rows: their stage history was never recorded. */
  historyKnown: boolean;
  hookCount: number;
  commentCount: number;
  fileCount: number;
  /** Null when no deck is attached yet; the card then draws its own placeholder. */
  presentation: BriefPresentation | null;
  /**
   * The concept's own 9:16 still, served from our own origin - a different
   * picture from the deck's 16:9 one, because the card in the editor's queue
   * is phone-shaped and a letterboxed slide looks wrong in it. Optional while
   * the readers that do not assemble it yet are still being extended.
   */
  conceptThumbUrl?: string | null;
  /**
   * The same still as signed Cloudflare copies, when ready and the CDN is on
   * (`CdnStill`): draw from these, fall back to `conceptThumbUrl`. Absent
   * when the viewer may not open the still, or nothing is uploaded yet.
   */
  conceptThumbCdn?: CdnStill | null;
  /** The first red must-do, for the card's one line of instruction. */
  firstMustDo: string | null;
  lastCommentAt: string | null;
  updatedAt: string;
  /**
   * How many times the concept has been sent back for revision; 0 until the
   * first. Optional while the readers that do not assemble it yet are still
   * being extended - absent means "not known", and a card shows nothing.
   */
  revisionRound?: number;
  /**
   * The storyboard the concept is briefed from, when it has one: what the
   * presentation editor opens and the deck view renders. Null when the concept
   * has no storyboard (an older brief, whose deck card keeps opening the
   * hosted HTML). Optional for the same reason as `revisionRound`.
   */
  storyboard?: BriefStoryboardLink | null;
  /**
   * When the concept reached each milestone, read from its event log. Optional
   * for the same reason as `revisionRound`: absent means "not assembled by this
   * reader", which is different from a milestone that has not happened (null).
   */
  dates?: ConceptDates;
}

/**
 * A concept's milestones, as ISO instants.
 *
 * Derived on read from `pipeline_events` (never stored twice), with one rule
 * per field so every screen and the pay-period summary agree:
 *
 * - `createdAt`: the brief row's own creation time.
 * - `releasedAt`: the first move into `assigned` that is not a step back from
 *   `in_progress` - the day the editor got the work. A concept taken back to
 *   strategist review is not released; released again, it counts from then.
 * - `firstDeliveredAt` / `lastDeliveredAt`: the first and the latest move into
 *   `in_review` that is a delivery - a step back from `pending_review` or an
 *   undone revision request is not one. The latest is the final
 *   delivery, the one that was approved.
 * - `approvedAt`: the latest approval still standing - null once it is undone
 *   (`approved -> pending_review`) until the concept is approved again, so an
 *   undone approval never counts in a pay period.
 * - `shippedAt`, `cancelledAt`: the latest move into that stage.
 *
 * Imported archive rows carry one `import` event that names a stage but not
 * when it was reached, so `import` events are never read as a milestone.
 */
export interface ConceptDates {
  createdAt: string;
  releasedAt: string | null;
  firstDeliveredAt: string | null;
  lastDeliveredAt: string | null;
  approvedAt: string | null;
  shippedAt: string | null;
  cancelledAt: string | null;
}

export type ConceptMilestone = "created" | "released" | "delivered" | "approved" | "shipped" | "cancelled";

/**
 * The one date a card shows: the milestone that put the concept where it is.
 * "Approved 3 Oct" on an approved card, "Created 1 Oct" on a new one. Falls
 * back to the creation date when the stage's own milestone was never logged.
 */
export function cardMilestone(stage: PipelineStage, dates: ConceptDates): { kind: ConceptMilestone; at: string } {
  const pick = (kind: ConceptMilestone, at: string | null) =>
    at ? { kind, at } : { kind: "created" as const, at: dates.createdAt };
  switch (stage) {
    case "strategist_review":
      return { kind: "created", at: dates.createdAt };
    case "assigned":
    case "in_progress":
      return pick("released", dates.releasedAt);
    case "in_review":
    case "changes_requested":
    case "pending_review":
      return pick("delivered", dates.lastDeliveredAt);
    case "approved":
      return pick("approved", dates.approvedAt);
    case "shipped":
      return dates.shippedAt ? { kind: "shipped", at: dates.shippedAt } : pick("approved", dates.approvedAt);
    case "cancelled":
      return pick("cancelled", dates.cancelledAt);
  }
}

/** The brief's storyboard, as much of it as a card or a header needs. */
export interface BriefStoryboardLink {
  id: number;
  slug: string;
  /** The live revision - what the presentation editor edits. */
  revision: number;
  /** The revision the editors' deck renders; null until the first publish. */
  publishedRevision: number | null;
  /**
   * Hooks written in the presentation: the opening line is Hook 1, and every
   * `hook` line is one more. What "Ready for editors" counts on a concept the
   * agent made, whose hooks live here rather than in the brief's hook rail.
   */
  hookCount: number;
  /** Whether the presentation has a line with words in it - the script, for the release guard. */
  hasScript: boolean;
  /** The presentation's own title, the video's name as the presentation editor shows it. Optional while older readers do not send it. */
  title?: string;
  /**
   * Revisions above the published one that still change what the editors
   * get: a change and its Discard or undo cancel out (`netChangeRevisions`).
   * Optional while older readers do not send it.
   */
  unsavedChanges?: number;
  /**
   * What the presentation shows, from its real content: `images_and_video`
   * once it is not images-only and at least one live shot has a clip chosen,
   * else `images`. Switches by itself when an editor adds the first video.
   * Lists only; optional while older readers do not send it.
   */
  mediaType?: "images" | "images_and_video";
}

/** True when a title says nothing but the concept code, like "CPT - 2106" or "ITR - 1981 - H1". */
export function isCodeOnlyTitle(title: string): boolean {
  const t = title.trim();
  return t === "" || /^(?:(?:CPT|ITR|H\d+|I\d+|\d+)[\s\-_|\u00b7.]*)+$/i.test(t);
}

/**
 * Notion's markdown marks as plain words: `**bold**`, `*italic*`, `_italic_`
 * and inline code. Imported notes and hooks carry them, and a line of copy
 * that prints its own asterisks reads as broken.
 */
export function stripMarks(text: string): string {
  return text
    .replace(/`([^`]*)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(^|[^\w*])\*([^*\n]+)\*(?![\w*])/g, "$1$2")
    .replace(/(^|[^\w_])_([^_\n]+)_(?![\w_])/g, "$1$2")
    // A pair Notion split across two blocks leaves half behind.
    .replace(/\*\*/g, "");
}

/** The words a card leads with: the title, unless it is only the code. */
export function briefHeadline(title: string, firstHook: string | null): string {
  if (!isCodeOnlyTitle(title)) return title;
  const hook = firstHook ? stripMarks(firstHook).replace(/\s+/g, " ").trim() : null;
  return hook ? hook : title;
}

/** The brief as a page. */
export interface BriefDetail extends BriefSummary {
  titleRaw: string;
  brand: string | null;
  frameworkId: number | null;
  frameworkName: string | null;
  /** The naming convention's three middle fields, as picker ids plus their labels. */
  techniqueId: number | null;
  techniqueName: string | null;
  angleId: number | null;
  angleName: string | null;
  /** Voice/talent: `MALE`, `FEMALE`, `MIX`, `N/A`. */
  gender: string | null;
  campaignTags: string[];
  parentBriefId: number | null;
  briefDate: DateOnly | null;
  deliveryFolderDriveId: string | null;
  namingConvention: string | null;
  /** Optimistic concurrency: sent back as If-Match on every write. */
  rowVersion: number;
  hooks: BriefHook[];
  notes: BriefNote[];
  script: BriefScript | null;
  refs: BriefRef[];
  files: BriefFile[];
  comments: BriefComment[];
  events: BriefEvent[];
  createdAt: string;
  /** The creator's name, for "Created by". */
  createdByName: string | null;
  /** What kind of concept it is; null until somebody chooses. */
  category: ConceptCategory | null;
  /**
   * The delivered files' names, one per hook, composed on the server. Null
   * when this reader is not shown them: before an editor and a strategist are
   * both assigned, from approval on, and to anybody but the concept's editor,
   * strategists and admins.
   */
  fileNames: ConceptFileNames | null;
}

/* ------------------------------------------------------------ notifications */

/**
 * What the portal tells people about.
 *
 * v1 delivers in-app only, but every one of these goes through one `notify()`
 * with a channel registry, so Slack and WhatsApp are an adapter rather than a
 * rewrite. The list is short on purpose: a bell that cries wolf gets muted, and
 * a muted bell is worse than no bell.
 */
export const NOTIFICATION_KINDS = [
  "assigned",
  "changes_requested",
  "delivered",
  "approved",
  "auto_approved",
  "mention",
  "overdue_digest",
  /** A strategist or admin set (or changed) where the editor's video goes. */
  "folder_assigned",
  /** A concept landed in strategist review - to the strategist who owns it, even when the agent's key created it. */
  "review_requested",
  /** A delivery was forwarded to the admin's final review - to admins. */
  "pending_review",
  /** Somebody wrote in the brief chat - to the other side of the conversation. */
  "comment",
  /** A strategist left or changed a note on a line of the presentation - to the editor, batched per brief. */
  "note_added",
  /** A clip the requester started finished after they left its popup - to them. Frames and voiceovers ring no bell. */
  "generation_done",
  /** A clip the requester started failed after they left its popup - to them, with the reason in plain words. */
  "generation_failed",
  /** Somebody suggested a concept on the Ideas page - to strategists and admins. */
  "idea_submitted",
  /** A strategist or admin approved an idea or did not - to the person who suggested it. */
  "idea_reviewed",
  /* Round 3 (D7): the catalogue in `notifications.ts` says who each one is for and whether it is on by default. */
  /** A video of theirs meets the admin's "potential winner" criteria - to its editor, once per video. */
  "potential_winner",
  /** A concept waiting for them was given to another editor or taken back - to the previous editor. */
  "unassigned",
  /** The editor started the concept - to its strategist. */
  "started",
  /** The editor delivered again after a revision request - to its strategist. */
  "resubmitted",
  /** The admin sent a concept back from final review - to its strategist. */
  "sent_back",
  /** An admin shipped a concept - to the admins. */
  "shipped",
  /** A concept waiting for them was taken back to New presentation - to its editor, who can no longer see it. */
  "concept_withdrawn",
  /** A concept was cancelled - to its editor, its strategist and the admins. */
  "cancelled",
  /** The month's AI spend reached 80% of the cap - to strategists and admins. */
  "spend_warning",
  /** The month's AI spend reached the cap - to strategists and admins. */
  "spend_reached",
  /** A generation failed or a clip was lost track of - to the admins. */
  "generation_alert",
  /** The Meta or tracker sync failed - to the admins. */
  "sync_failed",
  /** A WhatsApp message could not be delivered - to the admins. */
  "whatsapp_failed",
  /** Somebody signed in for the first time and is waiting for access - to the admins. */
  "user_signed_up",
  /** Monday's week in numbers for one editor - to that editor. */
  "weekly_results",
  /** Monday's pipeline in numbers - to the admins. */
  "weekly_pipeline",
  /* The cloud agents (scandi-cloud-agents CONTRACTS §17.3), sent by the studio's `notifyAgents`. */
  /** An ad job finished its draft presentation - to its owner. */
  "agent_job_ready",
  /** An ad job stopped without a presentation - to its owner and the admins. */
  "agent_job_failed",
  /** A chat or an ad job reached its spending guard and waits for an admin - to its owner and the admins. */
  "agent_budget_reached",
  /** A concept was published from an ad job - to the admins (a digest, off by default). */
  "agent_job_published",
  /** An ad job has been ready for 48 hours and nobody published it - to its owner, once. */
  "agent_job_unpublished",
  /** The AI cost ledger noticed something an admin should look at: our prices and Anthropic's disagree, a provider call never settled. */
  "agent_cost_alert",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/** Where a notification can go. Only `in_app` is wired up today. */
export const NOTIFICATION_CHANNELS = ["in_app", "slack", "whatsapp", "web_push"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const DELIVERY_STATUS = ["pending", "sending", "sent", "failed", "skipped"] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUS)[number];

export interface PipelineNotification {
  id: number;
  kind: NotificationKind;
  briefId: number | null;
  briefRef: string | null;
  briefTitle: string | null;
  actorUserId: number | null;
  actorName: string | null;
  payload: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

/* -------------------------------------------------------------- brief refs */

/**
 * The human key on a brief, e.g. `CPT-2139`.
 *
 * `CPT` is what the team already writes on every row; the number is the concept.
 * Kept as its own column rather than derived, because iterations and re-dos
 * share a concept number and the ref has to stay unique.
 */
export function formatBriefRef(conceptNumber: number, suffix?: string | null): string {
  return suffix ? `CPT-${conceptNumber}-${suffix.toUpperCase()}` : `CPT-${conceptNumber}`;
}

/** Null for anything that is not one of ours, so a bad URL 404s rather than 500s. */
export function parseBriefRef(ref: string): { conceptNumber: number; suffix: string | null } | null {
  const match = /^CPT-(\d{3,6})(?:-([A-Z0-9]{1,8}))?$/i.exec(ref.trim());
  if (!match) return null;
  return { conceptNumber: parseInt(match[1]!, 10), suffix: match[2]?.toUpperCase() ?? null };
}
